import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { CommunicationService } from '../../../service/api/communication.service';
import { ToastService } from '../../../ui/toast/toast.service';
import { HeaderButtonComponent } from '../../../ui/header-button/header-button.component';
import { ValidationEmailPayload } from '../../../models/validation-dialog.models';
import { ComparisonDialog } from '../../../ui/comparison-dialog/comparison-dialog';
import { NotificationDialogPayload } from '../../../models/notification-dialog.models';
import { NotificationDialog } from '../../../ui/notification-dialog/notification-dialog';
import {
  VerifyDataViolation,
  OrganizationStatus,
  DocumentComparison,
} from '../../../../core/models/organization.models';
import { OrganizationService } from '../../../service/api/organization.service';
import { AuthService } from '../../../../core/service/auth.service';
import { ValidationDialog } from '../../../ui/validation-dialog/validation-dialog';
import { AdminActionService } from '../../../service/admin-action.service';
import { CompareAction, VerifyDataFn } from '../../../service/admin-action.service';

@Component({
  selector: 'app-admin-buttons',
  imports: [HeaderButtonComponent, ValidationDialog, NotificationDialog, ComparisonDialog],
  templateUrl: './admin-buttons.html',
  styleUrl: './admin-buttons.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminButtons {
  private readonly organizationService = inject(OrganizationService);
  private readonly authService = inject(AuthService);
  private readonly communicationService = inject(CommunicationService);
  private readonly toastService = inject(ToastService);
  private readonly adminActionService = inject(AdminActionService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isAdmin = input<boolean>(false);
  readonly isStepPage = input<boolean>(false);
  readonly status = input<OrganizationStatus | null>(null);

  readonly isValidationDialogOpen = signal(false);
  readonly validationErrors = signal<VerifyDataViolation[]>([]);
  readonly validationTipoDocumento = signal<string | null>(null);
  readonly isValidating = signal(false);

  readonly isComparisonDialogOpen = signal(false);
  readonly comparisonData = signal<DocumentComparison[]>([]);
  readonly isComparing = signal(false);
  readonly comparisonViewMode = signal<'table' | 'single'>('table');
  readonly comparisonTitle = signal('Confronto Versioni Documenti');
  readonly comparisonDescription = signal(
    'Visualizza i campi modificati rispetto alla versione precedente.',
  );

  readonly isSendingValidation = signal(false);
  readonly isSendingCommunication = signal(false);

  readonly isCommunicationDialogOpen = signal(false);
  protected readonly activeOrganizationId = this.authService.activeOrganizationId;

  constructor() {
    this.adminActionService.triggerVerifica$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((overrideFn) => this.verifyData(overrideFn));

    this.adminActionService.triggerConfronta$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((action) => this.compareVersions(action));
  }

  readonly isVisible = computed(() => {
    return this.isAdmin() && this.isStepPage();
  });

  compareVersions(action: CompareAction) {
    if (this.isComparing()) {
      return;
    }

    if (action.kind === 'tipo') {
      this.compareTipo(action);
      return;
    }

    if (action.kind === 'section') {
      this.compareSection(action);
      return;
    }

    const versionId = this.status()?.version_id;
    if (!versionId) {
      this.toastService.error('ID versione non trovato');
      return;
    }

    this.isComparing.set(true);
    this.organizationService
      .compareVersion(versionId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isComparing.set(false)),
      )
      .subscribe({
        next: (response) => {
          this.comparisonViewMode.set('table');
          this.comparisonTitle.set('Confronto Versioni Documenti');
          this.comparisonDescription.set(
            'Visualizza i campi modificati rispetto alla versione precedente.',
          );
          this.comparisonData.set(response);
          this.isComparisonDialogOpen.set(true);
        },
        error: (err) => {
          console.error('Error comparing versions:', err);
        },
      });
  }

  private compareTipo(action: Extract<CompareAction, { kind: 'tipo' }>): void {
    this.isComparing.set(true);

    this.organizationService
      .compareTipo(action.sectionSlug, action.tipoDocumento)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isComparing.set(false)),
      )
      .subscribe({
        next: (response) => {
          this.comparisonViewMode.set('single');
          this.comparisonTitle.set('Confronto Tipo Documento');
          this.comparisonDescription.set(
            'Visualizza le differenze di questa tipologia rispetto alla versione precedente.',
          );
          this.comparisonData.set([
            {
              document_type: action.documentType,
              doc_subtype: response.tipo_documento,
              changed_fields: response.changed_fields,
              existed_in_previous_version: response.existed_in_previous_version,
              message: response.message,
            },
          ]);
          this.isComparisonDialogOpen.set(true);
        },
        error: (err) => {
          console.error('Error comparing tipo versions:', err);
        },
      });
  }

  private compareSection(action: Extract<CompareAction, { kind: 'section' }>): void {
    this.isComparing.set(true);

    action
      .compareFn()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isComparing.set(false)),
      )
      .subscribe({
        next: (response) => {
          this.comparisonViewMode.set('single');
          this.comparisonTitle.set('Confronto Sezione');
          this.comparisonDescription.set(
            'Visualizza le differenze di questa sezione rispetto alla versione precedente.',
          );
          this.comparisonData.set([
            {
              document_type: action.sectionSlug,
              doc_subtype: response.tipo_documento,
              changed_fields: response.changed_fields,
              existed_in_previous_version: response.existed_in_previous_version,
              message: response.message,
            },
          ]);
          this.isComparisonDialogOpen.set(true);
        },
        error: (err) => {
          console.error('Error comparing section versions:', err);
        },
      });
  }

  verifyData(overrideFn?: VerifyDataFn | null) {
    if (this.isValidating()) {
      return;
    }

    const apiFn: VerifyDataFn = overrideFn ?? (() => this.organizationService.verifyData());

    this.isValidating.set(true);
    apiFn()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isValidating.set(false)),
      )
      .subscribe({
        next: (response) => {
          this.validationErrors.set(response.violations);
          this.validationTipoDocumento.set(response.tipo_documento ?? null);
          this.isValidationDialogOpen.set(true);
        },
        error: (err) => {
          console.error('Error verifying data:', err);
        },
      });
  }

  onSendValidation(payload: ValidationEmailPayload) {
    if (this.isSendingValidation()) {
      return;
    }

    if (payload.messages.length === 0) {
      this.toastService.error('Nessun messaggio da inviare');
      return;
    }

    const versionId = this.status()?.version_id;
    if (!versionId) {
      this.toastService.error('ID versione non trovato');
      return;
    }

    this.isSendingValidation.set(true);
    this.organizationService
      .sendValidation(payload.messages, versionId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isSendingValidation.set(false)),
      )
      .subscribe({
        next: () => {
          this.isValidationDialogOpen.set(false);
          this.toastService.success(
            payload.messages.length === 1
              ? 'Email di validazione inviata con successo'
              : 'Email di validazione inviate con successo',
          );
        },
        error: (err) => {
          console.error('Error sending validation email:', err);
          this.toastService.error("Errore durante l'invio dell'email di validazione");
        },
      });
  }

  sendCommunication(payload: NotificationDialogPayload): void {
    if (this.isSendingCommunication()) {
      return;
    }

    const organizationId = this.activeOrganizationId();
    if (!organizationId) {
      console.error('No active organization ID found for personal communication');
      return;
    }

    this.isSendingCommunication.set(true);
    this.communicationService
      .sendPersonalNotification({
        message_subject: payload.subject,
        message_text: payload.message,
        organization_id: organizationId,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isSendingCommunication.set(false)),
      )
      .subscribe({
        next: () => {
          this.toastService.success('Comunicazione inviata con successo');
          this.isCommunicationDialogOpen.set(false);
        },
        error: (err) => {
          console.error('Error sending personal communication:', err);
          this.toastService.error("Errore durante l'invio della comunicazione");
        },
      });
  }
}
